import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import { useGetFlightDetailsQuery } from "@/redux/apis/clientFlight/clientFlightApi";
import { useGetGuestFlightDetailsQuery } from "@/redux/apis/guest/guestApi";
import { useAppSelector } from "@/redux/hooks";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { useFlightTrackingEnabled } from "./useFlightTrackingEnabled";

/** How old a cached flight may be before a joining subscriber refetches it. */
const STALE_AFTER_SECONDS = 60;

interface UseFlightDetailsArgs {
  flightNumber: string;
  /** Flight date, Y-m-d. */
  date: string;
  /** IATA of the booking airport — picks the matching leg on multi-leg flights. */
  airport?: string;
  /** Caller-side skip (e.g. no flight number captured, or not an airport pickup). */
  skip?: boolean;
}

/**
 * Scope-aware flight lookup — mirrors useBookingData. In the authed flow it hits the account-scoped
 * client endpoint (/v3/client/{id}/flight, DEV-474); in the guest flow it hits the provider-scoped
 * public twin (/v3/guest/provider/{uuid}/flight). Both return the same normalised FlightDetails,
 * so every consumer reads one contract regardless of scope. The inactive scope's query is skipped, so
 * a guest never fires the 401-ing client call and vice versa.
 *
 * MULTIPLE SUBSCRIBERS. The booking sheet holds one for its whole lifetime (`useSheetFlightPickup`) and
 * the date panel reads the same cache entry: RTK Query dedupes on the serialised argument, so extra
 * subscribers cost extra requests only if they build the argument differently — which is why both
 * callers derive the date through `flightDateFromPickup` and the airport through `extractAirportIata`.
 *
 * GATED HERE on the flight-tracking add-on rather than at each call site, because the gate protects the
 * REQUEST and callers should not be able to forget it. Deliberately on `enabled` alone, never
 * `enabled || isResolving`: `enabled` is false both when the add-on is off and while the operator profile
 * is still resolving, and holding through that window is the whole point — an operator that turns out to
 * have no add-on must never have had a request fired on its behalf (DEV-1503). Hoisting the query out of
 * the panel must not hoist it out from behind the gate. See {@link useFlightTrackingEnabled} for why
 * `isResolving` is a state of its own, and why anything that COMMITS still has to hold on it.
 */
export const useFlightDetails = ({ flightNumber, date, airport, skip }: UseFlightDetailsArgs) => {
  const { isGuest } = useGuestBooking();
  const { accountId } = useClientScope();
  const providerUuid = useAppSelector(operatorIdSelector);
  const { enabled: flightTrackingEnabled } = useFlightTrackingEnabled();

  const baseSkip = !!skip || !flightNumber || !date || !flightTrackingEnabled;

  // Refreshes the landing when a SUBSCRIBER MOUNTS onto an entry older than this — opening the Date/Time
  // step, or the tablet/mobile slide-in summary — and NOT on a timer. Nothing invalidates the Flight tag,
  // and the sheet root holds its subscription for the whole booking, so the entry is never evicted and
  // never refetches on its own: a sheet left open past the threshold with no panel mounting goes on
  // serving the landing fetched at mount. The threshold keeps the dedupe intact — a subscriber joining a
  // fresh entry still costs no request.
  const refetchOnMountOrArgChange = STALE_AFTER_SECONDS;

  const clientResult = useGetFlightDetailsQuery(
    { flightNumber, date, airport, accountId },
    { skip: baseSkip || isGuest, refetchOnMountOrArgChange }
  );

  const guestResult = useGetGuestFlightDetailsQuery(
    { providerUuid: providerUuid ?? "", flightNumber, date, airport },
    { skip: baseSkip || !isGuest || !providerUuid, refetchOnMountOrArgChange }
  );

  return isGuest ? guestResult : clientResult;
};
